import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@common-web/components/ui/dialog';
import {Button} from '@common-web/components/ui/button';
import {Info, Download} from 'lucide-react';
import {ReactNode, useCallback} from 'react';
import {api} from '@common-web/lib/trpc';
import {reportError} from '@common-web/lib/errorReporting';
import {triggerDownload} from '@client/pages/payroll/utils/exportUtils';

export function ConfiguratorGuide({triggerButton}: {triggerButton: ReactNode}) {
  const handleDownloadTemplate = useCallback(async () => {
    try {
      const {content, filename} = await api.group.payrollProcessing.getTemplate.query();
      triggerDownload(new Blob([content], {type: 'text/csv;charset=utf-8;'}), filename);
    } catch (error) {
      reportError(error, {
        title: 'Could not download template',
        context: 'ConfiguratorGuide.handleDownloadTemplate',
      });
    }
  }, []);

  return (
    <Dialog>
      <DialogTrigger asChild>{triggerButton}</DialogTrigger>
      <DialogContent className='sm:max-w-2xl max-h-[80vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle className={'text-internal-brand-navy'}>Payroll Mapping Guide</DialogTitle>
          <DialogDescription>
            Reference for data types, accepted values, and common mapping tips.
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-6'>
          <div>
            <h3 className='text-lg font-semibold text-internal-brand-navy mb-2 border-b border-gray-200 pb-2'>
              File Formats
            </h3>
            <ul className='list-disc pl-5 space-y-2 text-sm text-gray-600'>
              <li>CSV, XLSX, and XLS file types are supported.</li>
              <li>
                Up to 10 files can be uploaded at once, with a maximum file size of 50MB each.
              </li>
              <li>
                Columns do <span className='font-medium text-internal-brand-navy'>not</span> need to
                be in a specific order, and can be moved around as needed.
              </li>
            </ul>
            <div className={'mt-5 text-sm text-gray-600'}>
              Need a template to get started? Download a sample census file template below with all
              the fields required for payroll processing. You can use this as an optional starting
              point to format your own files for upload.
            </div>
            <Button
              variant='outline'
              size='sm'
              onClick={handleDownloadTemplate}
              className='mt-5 border-internal-brand-blue text-internal-brand-blue hover:bg-internal-brand-blue hover:border-internal-brand-blue hover:text-white font-medium'
            >
              <Download className='h-4 w-4 mr-2' />
              Download Template
            </Button>
          </div>
          <div>
            <h3 className='text-lg font-semibold text-internal-brand-navy mb-2 border-b border-gray-200 pb-2'>
              Parsing Rules
            </h3>
            <div className='space-y-3 text-sm text-gray-600'>
              <div>
                <div className='font-medium text-internal-brand-navy mb-1'>Yes/No Field</div>
                <div>
                  Accepted values (case-insensitive): Y, N, Yes, No, True, False, 1, 0, T, F. Values
                  are normalized to Y/N.
                </div>
              </div>
              <div>
                <div className='font-medium text-internal-brand-navy mb-1'>Pay Schedule</div>
                <div>
                  Values map to codes such as: W (Weekly: "weekly", "w", "52"), BW (Bi-Weekly:
                  "biweekly", "bw"), SM (Semi-monthly: "semimonthly", "sm", "24"), M (Monthly:
                  "monthly", "m", "12"), and special weekly variants "48" and "26".
                </div>
              </div>
              <div>
                <div className='font-medium text-internal-brand-navy mb-1'>Marital Status</div>
                <div>
                  Values map to codes: S (Single), M (Married), H (Head of Household), or E
                  (Exempt).
                </div>
              </div>
              <div>
                <div className='font-medium text-internal-brand-navy mb-1'>Gender</div>
                <div>
                  Values map to: M, F, O (Other). Examples include "male"/"m", "female"/"f", and
                  "other"/"o"/"unspecified".
                </div>
              </div>
              <div>
                <div className='font-medium text-internal-brand-navy mb-1'>Dates</div>
                <div>
                  Preferred formats: YYYY-MM-DD, MM/DD/YYYY, or M/D/YYYY. Time values are ignored.
                </div>
              </div>
              <div>
                <div className='font-medium text-internal-brand-navy mb-1'>Numbers</div>
                <div>
                  Commas and currency symbols are ignored when parsing; decimals are allowed.
                </div>
              </div>
            </div>
          </div>
          <div>
            <h3 className='text-lg font-semibold text-internal-brand-navy mb-2 pb-2 border-b border-gray-200'>
              Common Issues & Tips
            </h3>
            <ul className='list-disc pl-5 space-y-2 text-sm text-gray-600'>
              <li>
                Ensure all <span className='font-medium text-internal-brand-navy'>Required</span>{' '}
                fields are mapped before saving.
              </li>
              <li>For dates, use a consistent format across files to avoid parsing ambiguities.</li>
              <li>For Yes/No columns, standardize values to a common format (e.g., Y/N).</li>
              <li>Numeric columns should not include text; currency symbols and commas are OK.</li>
              <li>When linking files, ensure the chosen linking field exists in all files.</li>
            </ul>
          </div>
          <div>
            <h3 className='text-lg font-semibold text-internal-brand-navy mb-2 pb-2 border-b border-gray-200'>
              Advanced Options & Pipeline
            </h3>
            <div className='space-y-3 text-sm text-gray-600'>
              <div>
                <div className='font-medium text-internal-brand-navy mb-1'>
                  Using the advanced option menu
                </div>
                <div>
                  In{' '}
                  <span className='font-medium text-internal-brand-navy'>
                    Step 1: Column Transformations
                  </span>
                  , open the menu labeled{' '}
                  <span className='font-medium'>How to combine columns</span> to choose which
                  transformation you'd like to apply, such as math operators on different numeric
                  columns, or combining text field columns together. After selecting a
                  transformation, use <span className='font-medium'>Add column</span> to include two
                  or more source columns from your uploaded files.
                </div>
              </div>
              <div>
                <div className='font-medium text-internal-brand-navy mb-1'>Pipeline behavior</div>
                <div>
                  In{' '}
                  <span className='font-medium text-internal-brand-navy'>
                    Step 2: Value Transformations
                  </span>
                  , click <span className='font-medium'>Add new transformation</span> to open the
                  menu and choose a transformation. Each selection is applied sequentially from top
                  to bottom. Note that numeric column operations (Sum/Average/Min/Max) already parse
                  numbers internally, so additional value transformations like converting the value
                  to a number are not needed.
                </div>
              </div>
              <div>
                <div className='font-medium text-internal-brand-navy mb-1'>
                  Quick access for deduction totals
                </div>
                <div>
                  For the <span className='font-mono'>Post-tax Deductions Total</span> and{' '}
                  <span className='font-mono'>Pre-tax Deductions Total</span> fields, click the{' '}
                  advanced editor button to open a menu with options for{' '}
                  <span className='font-medium'>Advanced Editor</span> or{' '}
                  <span className='font-medium'>Combine deduction columns</span>. The latter is a
                  shortcut to sum multiple deduction columns into a single total.
                </div>
              </div>
              <div>
                <div className='font-medium text-internal-brand-navy mb-1'>
                  Example: Combining multiple deduction columns to arrive at the Post-tax Deductions
                  Total
                </div>
                <ol className='list-decimal pl-5 space-y-1'>
                  <li>
                    Select the checkbox for each column in the table that represents a post-tax
                    deduction in your payroll file that you want to include in the total, such as{' '}
                    <span className='font-mono'>Garnishment Amount</span>,{' '}
                    <span className='font-mono'>Charity</span>, or{' '}
                    <span className='font-mono'> Other Post-tax</span>.
                  </li>
                  <li>
                    Verify the result in the <span className='font-medium'>Live Preview</span>{' '}
                    section to ensure the summed values look correct.
                  </li>
                </ol>
              </div>
            </div>
          </div>
          <div>
            <h3 className='text-lg font-semibold text-internal-brand-navy mb-2 pb-2 border-b border-gray-200'>
              Handling New or Missing Columns
            </h3>
            <div className='space-y-3 text-sm text-gray-600'>
              <div>
                <div className='font-medium text-internal-brand-navy mb-1'>
                  Unmapped columns detected
                </div>
                <div>
                  If you upload files with extra columns, an alert will list them. Choose{' '}
                  <span className='font-medium'>Configure Columns</span> to map those you need, or
                  add them to the <span className='font-medium'>Ignored Columns</span> list if they
                  are not used.
                </div>
              </div>
              <div>
                <div className='font-medium text-internal-brand-navy mb-1'>
                  Required fields missing
                </div>
                <div>
                  If any <span className='font-medium'>Required</span> field is not mapped, you will
                  see an alert listing the missing fields. Click on the{' '}
                  <span className='font-medium'>Configuration</span> button to return to your file
                  configuration settings, and map each required field. Use{' '}
                  <span className='font-medium'>Suggestions</span> where available to quickly apply
                  a likely match.
                </div>
              </div>
              <div>
                <div className='font-medium text-internal-brand-navy mb-1'>
                  When your payroll provider renames columns
                </div>
                <div>
                  If your payroll provider renames, click on the{' '}
                  <span className='font-medium'>Configuration</span> button to return to your file
                  configuration settings. Locate the fields that are no longer mapped due to the
                  name change, and use the <span className='font-medium'>Suggestions</span> feature
                  to quickly find and map the new column names.
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
